import React, { useContext, useMemo } from "react";
import {
    Box,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { CandlestickSeriesOptions, RenkoSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import CommonChartSettingsTabs from './commonChartSettingsTabs';
type Props = {
    viewKey: string;
};
const RenkoSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    // const priceSettings = useMemo(() => {
    //     if (primarySeriesGroupId) {
    //         const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
    //         const series = chartConfig.series?.[seriesId ?? ''].options
    //         return (series as any)?.source ?? "close"
    //     }
    //     return "close"

    // }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const boxSizeMethodSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as RenkoSeriesOptions)?.boxMethod ?? 'atr'
        }
        return 'atr'
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const upColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const projectionUpColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as RenkoSeriesOptions
            color = series.projectionUpColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const projectionUpBorderColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as RenkoSeriesOptions
            color = series.projectionUpBorderColor ?? '#a9dcc3'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const projectionDownColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as RenkoSeriesOptions
            color = series.projectionDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const projectionDownBorderColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as RenkoSeriesOptions
            color = series.projectionDownBorderColor ?? '#f5a6ae'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const borderUpColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderUpColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const borderDownColor = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const wickUpColor = useMemo(() => {
        let color = '#26a69a'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.wickUpColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const wickDownColor = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.wickDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const wickVisible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.wickVisible ?? true
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof RenkoSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<CandlestickSeriesOptions>);
        }
    }

    const handleBooleanChange = (value: boolean, type: keyof CandlestickSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: value
            } as Partial<CandlestickSeriesOptions>);
        }
    };

    // const handleSourceChange = (newSource: 'close' | 'ohlc') => {
    //     if (chart) {
    //         chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
    //             source: newSource
    //         } as RenkoSeriesOptions)
    //     }
    // };

    const handleBoxSizeMethodChange = (method: 'atr' | 'traditional' | 'percentage') => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                boxMethod: method
            } as RenkoSeriesOptions)
        }
    }

    const boxSizeValue = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as RenkoSeriesOptions
            if (boxSizeMethodSettings === 'atr') return series.atr
            if (boxSizeMethodSettings === 'traditional') return series.boxSize
            return series.percentage
        }
    }, [boxSizeMethodSettings, chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleBoxSizeValueChange = (value: number) => {
        if (chart && value > 0) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [boxSizeMethodSettings === 'atr' ? 'atr' : boxSizeMethodSettings === 'percentage' ? 'percentage' : 'boxSize']: value
            } as RenkoSeriesOptions)
        }
    }

    const symbolTabContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Up bars</Typography>
                    <ColorSelector compact showOnlySwatch color={upColor.toHexString()} opacity={upColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'upColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={borderUpColor.toHexString()} opacity={borderUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'borderUpColor');
                        }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Down bars</Typography>
                    <ColorSelector compact showOnlySwatch color={downColor.toHexString()} opacity={downColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'downColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={borderDownColor.toHexString()} opacity={borderDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'borderDownColor');
                        }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Projection up bars</Typography>
                    <ColorSelector compact showOnlySwatch color={projectionUpColor.toHexString()} opacity={projectionUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'projectionUpColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={projectionUpBorderColor.toHexString()} opacity={projectionUpBorderColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'projectionUpBorderColor');
                        }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Projection down bars</Typography>
                    <ColorSelector compact showOnlySwatch color={projectionDownColor.toHexString()} opacity={projectionDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'projectionDownColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={projectionDownBorderColor.toHexString()} opacity={projectionDownBorderColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'projectionDownBorderColor');
                        }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <FormControlLabel sx={{ width: '160px', mr: '6px', marginLeft: '-6px', "& .MuiFormControlLabel-label": { fontSize: "14px" } }} control={
                        <Checkbox
                            checked={wickVisible}
                            onChange={(e) =>
                                handleBooleanChange(e.target.checked, "wickVisible" as any)
                            }
                        />} label="Wick" />
                    <ColorSelector compact showOnlySwatch color={wickUpColor.toHexString()} opacity={wickUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'wickUpColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={wickDownColor.toHexString()} opacity={wickDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'wickDownColor');
                        }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Box size method</Typography>
                    <FormControl size="small">
                        <Select
                            sx={{ height: '32px' }}
                            value={boxSizeMethodSettings}
                            onChange={(event) => handleBoxSizeMethodChange(event.target.value as 'atr' | 'traditional' | 'percentage')}
                        >
                            <MenuItem value="atr">ATR</MenuItem>
                            <MenuItem value="traditional">Traditional</MenuItem>
                            <MenuItem value="percentage">Percentage LTP</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>
                        {
                            boxSizeMethodSettings === 'atr' ?
                                'Atr Length' :
                                boxSizeMethodSettings === 'traditional' ?
                                    'Traditional' :
                                    'Percentage'
                        }
                    </Typography>
                    <FormControl size="small">
                        <TextField
                            fullWidth={false}
                            variant="outlined"
                            size="small"
                            value={boxSizeValue}
                            onChange={(event) => handleBoxSizeValueChange(Number(event.target.value))}
                            type="number"
                            sx={{
                                width: '80px',
                                '& .MuiInputBase-root': {
                                    height: '32px'
                                }
                            }}
                        />
                    </FormControl>
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default RenkoSettings;